import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import upperFirst from 'lodash/upperFirst';
import isNaN from 'lodash/isNaN';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { usePrevious } from 'rooks/dist/esm/hooks/usePrevious';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import { match } from 'ts-pattern';
import addHours from 'date-fns/addHours';
import addMinutes from 'date-fns/addMinutes';
import addDays from 'date-fns/addDays';
import { plural, t } from '@lingui/macro';

const durationUnits = ['hours', 'minutes', 'days', 'months'] as const;

const getDurationOptions = () => [
  {
    id: 'hours',
    singularLabel: t`Hour`,
    pluralLabel: t`Hours`,
  },
  {
    id: 'minutes',
    singularLabel: t`Minute`,
    pluralLabel: t`Minutes`,
  },
  {
    id: 'days',
    singularLabel: t`Day`,
    pluralLabel: t`Days`,
  },
  {
    id: 'months',
    singularLabel: t`Month`,
    pluralLabel: t`Months`,
  },
];

type DurationUnit = (typeof durationUnits)[number];

export interface TimeDuration {
  duration: number;
  unit: DurationUnit;
}

export const addDuration = (start: Date | number, td: TimeDuration) =>
  match(td.unit)
    .with('hours', () => addHours(start, td.duration))
    .with('minutes', () => addMinutes(start, td.duration))
    .with('days', () => addDays(start, td.duration))
    .with('months', () => addDays(start, td.duration))
    .otherwise(() => null);

export const getDuration = (
  start?: number,
  end?: number,
): TimeDuration | null => {
  if (!start || !end) return null;
  const secDuration = (end - start) / 1000;
  const minDuration = secDuration / 60;
  if (minDuration < 60)
    return { duration: Math.round(minDuration), unit: 'minutes' };
  const hrDuration = minDuration / 60;
  if (hrDuration < 24)
    return { duration: Math.round(hrDuration), unit: 'hours' };
  const daysDuration = hrDuration / 24;
  return { duration: Math.round(daysDuration), unit: 'days' };
};

export default function TimeDurationInputCombo(p: {
  disabled?: boolean;
  value?: TimeDuration | null;
  variant?: 'filled' | 'outlined';
  onChange?: (duration: TimeDuration | null) => void;
}) {
  const [duration, setDuration] = useState<null | number>(
    p.value?.duration ?? null,
  );
  const [unit, setUnit] = useState<DurationUnit | null>(p.value?.unit ?? null);
  const didRunOnce = useRef(false);

  const timeDuration = useMemo(() => {
    if (duration == null || unit == null) {
      return null;
    }
    return { duration, unit };
  }, [duration, unit]);

  const prevTimeDuration = usePrevious(timeDuration);

  useEffect(() => {
    if (didRunOnce.current) {
      if (!isEqual(timeDuration, prevTimeDuration)) {
        p.onChange?.(timeDuration);
      }
    } else {
      didRunOnce.current = true;
    }
  }, [timeDuration]);

  return (
    <>
      <Grid item xs={3}>
        <TextField
          value={duration ?? ''}
          size='small'
          disabled={p.disabled}
          onChange={({ target: { value } }) => {
            if (isEmpty(value)) setDuration(null);
            const numValue = Number(value);
            if (!isNaN(numValue)) setDuration(numValue);
          }}
        />
      </Grid>
      <Grid item xs={6}>
        <Select
          variant={p.variant}
          fullWidth
          value={unit}
          size='small'
          onChange={({ target: { value } }) => {
            setUnit(value as DurationUnit | null);
          }}
          disabled={p.disabled}
        >
          {getDurationOptions().map((u) => (
            <MenuItem key={u.id} value={u.id}>
              {upperFirst(
                plural(duration ?? 2, {
                  one: u.singularLabel,
                  other: u.pluralLabel,
                }),
              )}
            </MenuItem>
          ))}
        </Select>
      </Grid>
    </>
  );
}
